/* ============================================================================
   os-cart-promise.js — delivery promise, trust badges and ship-cutoff countdown
   in the cart drawer.
   ----------------------------------------------------------------------------
   THIS IS A PORT, NOT AN INVENTION.

   The Intelligems experiment "Delivery promise in cart drawer"
   (a1fe37f7-8d3d-4e96-9f4b-7bbdedeea20c, variation aa566e1d "Delivery promise")
   ran 14 days against a 50/50 control and won:

     conversion rate        0.996% -> 1.158%   +16.2%   p2bb 0.97
     revenue per visitor    $2.18  -> $2.57    +17.7%   p2bb 0.92
     checkout abandonment   42.3%  -> 37.2%             p2bb 0.95
     estimated impact       +$51k / month

   The logic below is that variation's customJs, ported so the test can be
   stopped and the behaviour kept. The date maths, the fail-safe rules, the
   overlapping-resolve guard, the signature check and the event wiring are all
   as they ran. Changing them changes what was measured. What IS new here:

     1. presentation - brand tokens, the shared inline-SVG icon set, and no
        emoji (assets/os-cart-promise.css)
     2. trust badges, replacing the standalone guarantee line
     3. the ship-cutoff countdown, which is gated behind config AND a window

   ----------------------------------------------------------------------------
   THE ONE RULE THAT GOVERNS EVERYTHING BELOW: WHEN IN DOUBT, RENDER NOTHING.

   resolvePromise() returns null - meaning paint no dates at all - if /cart.js
   fails, if any product lookup fails, or if a preorder line has no parseable
   ship: tag. That is deliberate and load-bearing. A missing delivery promise
   is a neutral outcome. A wrong delivery promise on a $198 order of perishable
   frozen seafood is not. Every code path that cannot prove a date must fall
   into the null branch. Preserve this.
   ============================================================================ */

(function () {
  'use strict';

  if (window.__osCartPromise) { return; }
  window.__osCartPromise = true;

  /* --- Config from theme settings, emitted by snippets/css-include.liquid.
     Absent or malformed config must not break the promise block, so every
     field is defaulted here rather than assumed. */
  var CFG = window.OS_CART_PROMISE || {};
  var COUNTDOWN_ON = CFG.countdown === true;
  var CUTOFF_HOUR = parseInt(CFG.cutoffHour, 10);
  if (isNaN(CUTOFF_HOUR) || CUTOFF_HOUR < 0 || CUTOFF_HOUR > 23) { CUTOFF_HOUR = 0; }

  /* --- The store clock ---------------------------------------------------
     The cutoff is a FULFILMENT fact and must be evaluated in the fulfilment
     timezone, never in the shopper's browser. At hour 0 the browser's clock
     was nearly harmless - everyone's midnight is the natural rollover. At a
     6am cutoff it is a real defect: Alaska and Eastern are four hours apart,
     so at 08:00 Eastern a New York shopper would be told they missed a cutoff
     that is still two hours away in the warehouse's own clock.

     Confirmed by Lauren 2026-09-17: fulfilment runs on Pacific time. The value
     is emitted by snippets/css-include.liquid (os_cart_store_tz) so cart, PDP
     and config share one source; the default here is only a backstop. */
  var STORE_TZ = (typeof CFG.storeTz === 'string' && CFG.storeTz) ? CFG.storeTz : 'America/Los_Angeles';

  /* Returns a Date whose LOCAL fields (getDay/getHours/getDate) read as the
     store's wall clock. It is therefore "store-shifted": it must only ever be
     compared against other store-shifted Dates (getShipDate, cutoffBefore),
     never against Date.now(), because the two live in different wall-clock
     spaces. Within store-shifted space the difference of two Dates is still
     the true elapsed duration, which is all the countdown needs. */
  function nowInStoreTz() {
    try {
      return new Date(new Date().toLocaleString('en-US', { timeZone: STORE_TZ }));
    } catch (e) {
      return new Date();
    }
  }
  function nowStoreMs() { return nowInStoreTz().getTime(); }

  /* How close to the cutoff the countdown is allowed to appear.

     WHY THERE IS A WINDOW AT ALL. The ship date is already printed, in full,
     on the line directly above the countdown. For most of the week the
     countdown therefore says nothing new - "order within 5d 9h to ship
     Tuesday" sitting under "Ships: Tuesday, September 22" is the same fact
     twice, and a multi-day number is not urgency, it is furniture. Inside the
     window it becomes the one thing the reader cannot work out for themselves:
     whether they are about to miss this week's boat.

     So it is silent Tuesday through Saturday and appears for the last stretch
     before the cutoff. That is also the honest shape: a countdown that runs
     permanently reads as a manufactured clock, which is exactly what this
     store does not want to be. */
  var COUNTDOWN_WINDOW_H = parseInt(CFG.countdownWindowHours, 10);
  if (isNaN(COUNTDOWN_WINDOW_H) || COUNTDOWN_WINDOW_H < 1) { COUNTDOWN_WINDOW_H = 48; }
  var COUNTDOWN_WINDOW_MS = COUNTDOWN_WINDOW_H * 3600000;

  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  function fmtFull(d) { return DAYS[d.getDay()] + ', ' + MONTHS[d.getMonth()] + ' ' + d.getDate(); }
  function fmtShort(d) { return MONTHS[d.getMonth()] + ' ' + d.getDate(); }

  /* --- Icons -------------------------------------------------------------
     The SVGs are NOT duplicated here. snippets/css-include.liquid renders them
     once from snippets/os-lp-icon.liquid into an inert template element, and
     this clones out of it. One source for the artwork, and the icons inherit
     currentColor so they can never fail contrast against whatever ground they
     land on.

     Missing template, missing icon, or a browser without template support
     all resolve to null, and every caller then simply omits the icon rather
     than throwing. Same philosophy as the date rules: degrade, never break. */
  function icon(name) {
    try {
      var tpl = document.getElementById('os-cart-icons');
      if (!tpl || !tpl.content) { return null; }
      var svg = tpl.content.querySelector('[data-os-icon="' + name + '"] svg');
      return svg ? svg.cloneNode(true) : null;
    } catch (e) { return null; }
  }

  /* --- The ship rule -----------------------------------------------------
     KEPT IDENTICAL to getShipDate() in snippets/delivery-estimate.liquid.
     Cart and PDP must never disagree, so if you touch one, touch the other
     in the same commit.

     Ships every Monday and Tuesday. Updated 2026-10-26 per Lauren: the weekly
     cutoff is 6am Tuesday, Eastern (store timezone). Each ship day's boat
     closes at CUTOFF_HOUR:00 on that day.
       Mon before CUTOFF_HOUR:00     -> that same Monday
       Mon at/after CUTOFF_HOUR:00   -> Tuesday
       Tue before CUTOFF_HOUR:00     -> that same Tuesday
       Tue at/after CUTOFF_HOUR:00   -> next Monday
       Wed - Sun                     -> next Monday

     Evaluated in the store timezone, not the browser's (see nowInStoreTz).
     Returns a store-shifted Date carrying the ship date at 00:00. */
  function getShipDate() {
    var now = nowInStoreTz();
    var day = now.getDay();               // 0=Sun .. 6=Sat, in store tz
    var beforeCutoff = now.getHours() < CUTOFF_HOUR;
    var offset;
    if (day === 1 && beforeCutoff) {
      offset = 0;                         // Mon before cutoff -> this Monday
    } else if (day === 1 || (day === 2 && beforeCutoff)) {
      offset = 2 - day;                   // Mon after cutoff / Tue before cutoff -> this Tuesday
    } else {
      offset = (8 - day) % 7;             // Tue after cutoff, Wed - Sun -> next Monday
    }
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset, 0, 0, 0, 0);
  }

  function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  }

  /* --- The cutoff --------------------------------------------------------
     The ship-day rule gives a ship DAY; the cutoff hour gives the time of day,
     ON that ship day, by which an order must be in. Confirmed by Lauren
     2026-09-17: each ship day closes at 6am Eastern on that day (Mon, Tue).

     The roll to next week now lives inside getShipDate() itself - so the ship
     day is already correct before it reaches here, the PDP snippet gets the
     same behaviour from the same function, and there is no longer any window
     in which cart and PDP disagree. cutoffBefore() is therefore just the
     display/countdown instant: CUTOFF_HOUR:00 on the ship day.

     STORE-SHIFTED SPACE. cutoffBefore returns a store-shifted Date (its fields
     are the store wall clock), so it must only be compared against
     nowInStoreTz()/nowStoreMs(), never against Date.now(). Hour 0 gives
     midnight entering the ship day, which is exactly where getShipDate flips,
     so the default still invents nothing. */
  function cutoffBefore(shipDay) {
    return new Date(shipDay.getFullYear(), shipDay.getMonth(), shipDay.getDate(), CUTOFF_HOUR, 0, 0, 0);
  }

  function effectiveShipDate() {
    return startOfDay(getShipDate());
  }

  /* Never "0m", never seconds. A countdown that ticks down to zero in front of
     someone is pressure theatre; this one exists to answer "have I missed this
     week's boat", which is a question about hours. Returns null when the
     cutoff has passed, and the caller then renders no countdown at all rather
     than a lie. */
  function remainingText(ms) {
    if (!(ms > 0)) { return null; }
    var mins = Math.floor(ms / 60000);
    var hours = Math.floor(mins / 60);
    var days = Math.floor(hours / 24);
    if (days >= 1) { return days + 'd ' + (hours - days * 24) + 'h'; }
    if (hours >= 1) { return hours + 'h ' + (mins - hours * 60) + 'm'; }
    return Math.max(1, mins) + 'm';
  }

  /* --- Preorder detection -------------------------------------------------
     preorder-pdp products carry tags 'preorder' and 'ship:YYYY-MM-DD'.
     Reading tags per cart line means this follows merchandising automatically:
     flip a product in or out of preorder in Shopify and the cart follows,
     with no change here. */
  var tagCache = {};
  function getTags(handle) {
    if (Object.prototype.hasOwnProperty.call(tagCache, handle)) { return Promise.resolve(tagCache[handle]); }
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) { throw new Error('bad status'); } return r.json(); })
      .then(function (p) { tagCache[handle] = (p && p.tags) ? p.tags : []; return tagCache[handle]; })
      .catch(function () { tagCache[handle] = null; return null; });
  }
  function isPreorder(tags) {
    for (var i = 0; i < tags.length; i++) { if (/^pre-?order$/i.test(String(tags[i]).trim())) { return true; } }
    return false;
  }
  function shipTagDate(tags) {
    for (var i = 0; i < tags.length; i++) {
      var m = /^ship:(\d{4})-(\d{2})-(\d{2})$/.exec(String(tags[i]).trim());
      if (m) {
        var d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
        if (!isNaN(d.getTime())) { return d; }
      }
    }
    return null;
  }

  /* Resolves to a promise descriptor, or null meaning RENDER NOTHING. */
  function resolvePromise() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) { throw new Error('bad status'); } return r.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        if (!items.length) { return null; }
        var handles = [];
        for (var i = 0; i < items.length; i++) {
          var h = items[i].handle;
          if (h && handles.indexOf(h) === -1) { handles.push(h); }
        }
        if (!handles.length) { return null; }
        return Promise.all(handles.map(getTags)).then(function (all) {
          var preDates = [], anyPre = false, anyStandard = false;
          for (var i = 0; i < all.length; i++) {
            var t = all[i];
            if (t === null) { return null; }            /* lookup failed -> say nothing */
            if (isPreorder(t)) {
              anyPre = true;
              var d = shipTagDate(t);
              if (!d) { return null; }                  /* preorder with no usable date -> say nothing */
              preDates.push(d);
            } else {
              anyStandard = true;
            }
          }
          if (anyPre) {
            /* Mixed cart: latest date wins. The order ships together, and the
               latest date is also the safe direction - nothing can arrive later
               than promised; a split shipment just arrives early. */
            preDates.sort(function (a, b) { return b.getTime() - a.getTime(); });
            return { mode: 'preorder', date: preDates[0], mixed: anyStandard };
          }
          var s = effectiveShipDate();
          var a1 = new Date(s); a1.setDate(s.getDate() + 1);
          var a2 = new Date(s); a2.setDate(s.getDate() + 2);
          return { mode: 'standard', ship: s, a1: a1, a2: a2, cutoff: cutoffBefore(s) };
        });
      })
      .catch(function () { return null; });
  }

  /* --- Rendering ---------------------------------------------------------- */

  function datesHtml(p) {
    if (p.mode === 'preorder') {
      var h = '<div><strong>Pre-order</strong> &mdash; Estimated shipping: ' + fmtShort(p.date) + '</div>';
      if (p.mixed) { h += '<div class="os-cart-promise__note">Everything in your order ships together on this date.</div>'; }
      return h;
    }
    return '<div><strong>Ships:</strong> ' + fmtFull(p.ship) + '</div>' +
      '<div><strong>Estimated delivery:</strong> ' + fmtFull(p.a1) + '&ndash;' + fmtFull(p.a2) + '</div>';
  }

  /* Is the countdown allowed to be on screen RIGHT NOW?

     Three gates, all of which must pass: the feature is switched on, this is
     the standard Tuesday path, and the cutoff is both still ahead and inside
     the window.

     A preorder cart shows no countdown at all, by design and for the same
     reason as every other rule in this file: the Tuesday cutoff does not apply
     to a preorder line, whose date comes from its ship: tag, so a countdown
     beside it would be counting down to a deadline that governs nothing and
     would visibly contradict the date printed directly above it.

     This is time-dependent, which is why signature() below folds it in: the
     block has to repaint when the window opens, even though nothing about the
     cart or the ship date changed. */
  function countdownMs(p) {
    if (!COUNTDOWN_ON) { return null; }
    if (!p || p.mode !== 'standard' || !p.cutoff) { return null; }
    var ms = p.cutoff.getTime() - nowStoreMs();
    if (!(ms > 0) || ms > COUNTDOWN_WINDOW_MS) { return null; }
    return ms;
  }

  function cutoffEl(p) {
    var ms = countdownMs(p);
    if (ms === null) { return null; }
    var left = remainingText(ms);
    if (!left) { return null; }

    var row = document.createElement('div');
    row.className = 'os-cart-promise__cutoff';
    var ico = icon('clock');
    if (ico) { ico.setAttribute('class', 'os-cart-promise__cutoff-ico'); row.appendChild(ico); }
    var txt = document.createElement('span');
    txt.innerHTML = 'Order within <strong data-os-countdown>' + left + '</strong> to ship ' +
      DAYS[p.ship.getDay()] + '.';
    row.appendChild(txt);
    return row;
  }

  function promiseEl(p) {
    var el = document.createElement('div');
    el.className = 'os-cart-promise';
    el.setAttribute('data-os', 'cart-promise');
    el.setAttribute('data-os-mode', p.mode);

    var ico = icon('truck');
    if (ico) { ico.setAttribute('class', 'os-cart-promise__ico'); el.appendChild(ico); }

    var body = document.createElement('div');
    body.className = 'os-cart-promise__body';
    body.innerHTML = '<div class="os-cart-promise__dates">' + datesHtml(p) + '</div>' +
      '<div class="os-cart-promise__how">Flash-frozen, vacuum-sealed and packed with dry ice &mdash; it arrives frozen solid.</div>';
    el.appendChild(body);

    var cut = cutoffEl(p);
    if (cut) { body.appendChild(cut); }
    return el;
  }

  /* Two claims, trimmed from four 2026-09-18 (Lauren): the dropped two were the
     only ones already stated elsewhere in the same drawer - "Ships frozen with
     dry ice" repeats the delivery-promise line below, and "Wild-caught in Alaska"
     is on the page throughout. What stays are the two strongest at the payment
     step, neither duplicated here: free shipping and the 30-day guarantee (the
     variation's measured guarantee line). Re-add box/snowflake+fish to revert. */
  var BADGES = [
    { icon: 'box', text: 'Free shipping on every order' },
    { icon: 'shield', text: 'Try it risk free for 30 days' }
  ];

  function badgesEl(variant) {
    var ul = document.createElement('ul');
    ul.className = 'os-cart-badges' + (variant ? ' os-cart-badges--' + variant : '');
    ul.setAttribute('data-os', 'cart-badges');
    for (var i = 0; i < BADGES.length; i++) {
      var li = document.createElement('li');
      li.className = 'os-cart-badges__item';
      var ico = icon(BADGES[i].icon);
      if (ico) { li.appendChild(ico); }
      var span = document.createElement('span');
      span.textContent = BADGES[i].text;
      li.appendChild(span);
      ul.appendChild(li);
    }
    return ul;
  }

  /* The signature is what stops the drawer repainting on every mutation, so
     anything that changes what the block LOOKS like has to be in it - not just
     what the cart contains. The countdown's window is time-dependent, so it is
     folded in here: without it, the block would render once on Saturday with
     no countdown and never repaint when the window opened on Sunday night. */
  function signature(p) {
    if (!p) { return 'none'; }
    if (p.mode === 'preorder') { return 'pre|' + p.date.getTime() + '|' + (p.mixed ? 1 : 0); }
    return 'std|' + p.ship.getTime() + '|' + (countdownMs(p) === null ? 0 : 1);
  }

  /* Unconditional edits: safe with no knowledge of cart contents.

     The badges live here rather than in paint() on purpose. They make no claim
     about dates, so they do not need the cart resolved and must not disappear
     when a lookup fails - unlike everything in paint(), which does. */
  function applySync() {
    var msgs = document.querySelectorAll('p.cart__message');
    for (var i = 0; i < msgs.length; i++) {
      var m = msgs[i];
      if (m.getAttribute('data-os-msg')) { continue; }
      if (!/Taxes\s+and/i.test(m.textContent || '')) { continue; }
      var link = m.querySelector('a[href*="shipping-policy"]');
      var href = link ? link.getAttribute('href') : '/policies/shipping-policy';
      m.innerHTML = 'Taxes calculated at checkout &middot; <a href="' + href + '">Shipping</a> always free.';
      m.setAttribute('data-os-msg', '1');
    }
    /* --- Where the badges mount ------------------------------------------
       TOP OF THE CART, as the first thing inside .cart__wrapper. That is the
       slot the free shipping bar used to occupy, so the drawer gains no height
       from this - it is a swap, not an addition.

       This is a change from the first build, which put them under the checkout
       button on the reasoning that anything above the button pushes it toward
       the fold. That reasoning still holds in general; it is overridden here
       because the badges took over a slot that was already spending that
       height. Do not add a THIRD block above the button.

       .cart__actions is kept as a fallback for any surface that renders
       cart-details.liquid without the drawer's .cart__wrapper around it. */
    var mounted = false;
    var wraps = document.querySelectorAll('.cart__wrapper');
    for (var k = 0; k < wraps.length; k++) {
      var w = wraps[k];
      if (w.querySelector('[data-os="cart-badges"]')) { mounted = true; continue; }
      w.insertBefore(badgesEl('top'), w.firstChild);
      mounted = true;
    }
    if (mounted) { return; }
    var acts = document.querySelectorAll('.cart__actions');
    for (var n = 0; n < acts.length; n++) {
      if (acts[n].querySelector('[data-os="cart-badges"]')) { continue; }
      acts[n].appendChild(badgesEl());
    }
  }

  /* Date block: only painted once the cart is actually resolved. */
  function paint(p) {
    var sig = signature(p);
    var ctrls = document.querySelectorAll('.cart__controls');
    for (var j = 0; j < ctrls.length; j++) {
      var c = ctrls[j];
      var existing = c.querySelector('[data-os="cart-promise"]');
      if (!p) { if (existing) { existing.parentNode.removeChild(existing); } continue; }
      if (existing && existing.getAttribute('data-os-sig') === sig) { continue; }
      var fresh = promiseEl(p);
      fresh.setAttribute('data-os-sig', sig);
      if (existing) { existing.parentNode.replaceChild(fresh, existing); }
      else {
        var det = c.querySelector('.cart__details');
        if (det) { c.insertBefore(fresh, det); } else { c.insertBefore(fresh, c.firstChild); }
      }
    }
  }

  var token = 0, inflight = false, pendingAgain = false, lastPromise = null;
  function refresh() {
    if (inflight) { pendingAgain = true; return; }
    inflight = true;
    var mine = ++token;
    resolvePromise().then(function (p) {
      inflight = false;
      if (mine !== token) { return; }               /* a newer resolve superseded this */
      lastPromise = p;
      /* applySync here as well as in schedule(): a cart mutation is the moment
         .cart__actions is re-rendered, and this path is driven by cart events
         and timers rather than by rAF, so the badges remount even on a tab
         where the frame callback is being throttled. It is idempotent - every
         edit it makes is guarded by a marker attribute. */
      try { applySync(); } catch (e) {}
      paint(p);
      if (pendingAgain) { pendingAgain = false; refresh(); }
    }).catch(function () {
      inflight = false; lastPromise = null;
      try { applySync(); } catch (e) {}
      paint(null);
      if (pendingAgain) { pendingAgain = false; refresh(); }
    });
  }

  /* --- schedule() ---------------------------------------------------------
     Coalesces a burst of DOM mutations into one pass.

     THE BACKSTOP IS NOT OPTIONAL. Chrome does not run requestAnimationFrame in
     a hidden or background tab, and the queued flag is set BEFORE the callback.
     With rAF alone, a page that loads in a background tab - opened in a new
     tab, restored on startup, prefetched, or simply sitting behind another
     window - sets queued to true, never runs the callback, and every later
     schedule() call returns immediately at the guard. The badges then never
     mount for the whole life of that tab. This was observed, not theorised.

     So a setTimeout races the rAF and whichever arrives first wins; done makes
     the loser a no-op. setTimeout is throttled in background tabs but it is
     not suspended, so the pass always lands. */
  var queued = false;
  function schedule() {
    if (queued) { return; }
    queued = true;
    var done = false;
    var run = function () {
      if (done) { return; }
      done = true;
      queued = false;
      try {
        applySync();
        /* Repaint the last known state so a re-rendered drawer keeps the block. */
        if (lastPromise) { paint(lastPromise); }
      } catch (e) {}
    };
    (window.requestAnimationFrame || function (f) { setTimeout(f, 16); })(run);
    setTimeout(run, 250);
  }

  /* --- The countdown tick -------------------------------------------------
     Writes textContent into whatever [data-os-countdown] is in the document
     RIGHT NOW. It deliberately holds no reference to any element: the drawer
     re-renders constantly, and a tick holding a stale node would keep updating
     a detached element while the live one froze - the exact failure that took
     assets/os-lp-reveal.js down before it was rewritten the same way.

     The paint(p) call is what opens the window: paint is a no-op whenever the
     signature already matches, so calling it here costs a querySelectorAll and
     an attribute compare, and it is the one thing that makes the countdown
     appear the moment the window opens without waiting for a cart mutation.

     No class is toggled and no element is moved, so this can never start a
     transition on a cloned node.

     Once the cutoff passes, rather than showing zero it hands off to refresh(),
     which recomputes the ship date - and because the rule rolls to the next
     Tuesday at exactly that moment, the block repaints with the following
     week's date and, a week later, a fresh and honest countdown. */
  function tick() {
    if (!COUNTDOWN_ON) { return; }
    var p = lastPromise;
    if (!p || p.mode !== 'standard' || !p.cutoff) { return; }
    if (p.cutoff.getTime() - nowStoreMs() <= 0) { refresh(); return; }
    paint(p);
    var ms = countdownMs(p);
    if (ms === null) { return; }
    var left = remainingText(ms);
    if (!left) { return; }
    var nodes = document.querySelectorAll('[data-os-countdown]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].textContent !== left) { nodes[i].textContent = left; }
    }
  }

  function boot() {
    schedule(); refresh();
    try { new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true }); } catch (e) {}
     ['cart:updated', 'cart:refresh', 'cart:open', 'cart:change', 'cart:added', 'on:cart:loaded', 'rebuy:cart.add', 'rebuy:cart.change'].forEach(function (ev) {
      document.addEventListener(ev, function () { schedule(); refresh(); });
    });
    /* Re-resolve across a date rollover so a long-lived tab cannot go stale. */
    setInterval(refresh, 300000);
    if (COUNTDOWN_ON) { setInterval(tick, 15000); }
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); } else { boot(); }
})();
